






import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { StaggereableProperty } from './components.interfaces';

interface TransitionGroupHTMLElement extends HTMLElement {
  __EBX_POSITION: {
    left: number;
    top: number;
  };
}

@Component
export default class StaggeringTransitionGroup extends Vue {
  @Prop({ default: 50 })
  private stagger: number;

  @Prop({ default: () => ['enter', 'move', 'leave'] })
  private staggerProperties: StaggereableProperty[];

  get transitionGroupProps() {
    const { stagger, staggerProperties, ...props } = this.$attrs;
    return props;
  }

  get transitionGroupEvents() {
    const events = { ...this.$listeners };
    events['before-leave'] = this.arrarize(events['before-leave'], this.restorePosition);
    return events;
  }

  get animationClasses() {
    const name = this.$attrs.name;
    return {
      enter: this.$attrs.enterClass || `${ name }-enter`,
      leave: this.$attrs.leaveClass || `${ name }-leave`,
      move: this.$attrs.moveClass || `${ name }-move`
    };
  }

  mounted() {
    const groupedElements = this.getGroupedElements();
    this.applyDelayToElements(groupedElements);
  }

  beforeUpdate() {
    const targetElements = this.$el ? Array.from(this.$el.children) as TransitionGroupHTMLElement[] : [];
    targetElements.forEach(this.removeDelay);
    targetElements.forEach(this.storePosition);
  }

  updated() {
    const groupedElements = this.getGroupedElements();
    this.applyDelayToElements(groupedElements);
  }

  getGroupedElements() {
    const targetElements = this.$el ? Array.from(this.$el.children) as TransitionGroupHTMLElement[] : [];
    return targetElements.reduce<Record<StaggereableProperty, TransitionGroupHTMLElement[]>>((accumulated, domElement) => {
      if (domElement.classList.contains(this.animationClasses.leave)) {
        accumulated.leave.push(domElement);
      } else if (domElement.classList.contains(this.animationClasses.enter)) {
        accumulated.enter.push(domElement);
      } else if (domElement.classList.contains(this.animationClasses.move)) {
        accumulated.move.push(domElement);
      }
      return accumulated;
    }, { move: [], enter: [], leave: [] });
  }

  isStaggered(event: StaggereableProperty) {
    return this.staggerProperties.includes(event);
  }

  applyDelayToElements({ leave, move, enter }: Record<StaggereableProperty, TransitionGroupHTMLElement[]>) {
    let staggeringIndex = 0;
    if (this.isStaggered('leave')) {
      leave.forEach(el => this.addDelay(el, staggeringIndex++));
    }

    if (this.isStaggered('move')) {
      move.forEach(el => this.addDelay(el, staggeringIndex++));
    }

    if (this.isStaggered('enter')) {
      enter.forEach(el => this.addDelay(el, staggeringIndex++));
    }
  }

  addDelay(element: TransitionGroupHTMLElement, staggeringIndex: number) {
    element.style.transitionDelay = `${ staggeringIndex * this.stagger }ms`;
  }

  removeDelay(element: TransitionGroupHTMLElement) {
    element.style.transitionDelay = '';
  }

  storePosition(element: TransitionGroupHTMLElement) {
    element.__EBX_POSITION = {
      left: element.offsetLeft,
      top: element.offsetTop
    };
  }

  restorePosition(element: TransitionGroupHTMLElement) {
    if (element.__EBX_POSITION) {
      const { top, left } = element.__EBX_POSITION;
      element.style.top = `${ top }px`;
      element.style.left = `${ left }px`;
    }
  }

  arrarize<T>(possibleArray: T | T[], ...itemsToPush: T[]) {
    if (Array.isArray(possibleArray)) {
      return [...itemsToPush, ...possibleArray];
    } else if (possibleArray) {
      return [...itemsToPush, possibleArray];
    } else {
      return [...itemsToPush];
    }
  }
}
